import {
  AISLE_WIDTH,
  DEFAULT_WALL_HEIGHT,
  HALL_G_MAX_HEIGHT,
  WALL_HEIGHTS,
  type WallHeight,
} from '../config/booth';
import type { Stats } from '../sim/types';

export type ViewMode = 'walk' | 'plan';

export interface PanelHandlers {
  onView(mode: ViewMode): void;
  onCrowd(level: number): void;
  onHeight(h: WallHeight): void;
  onRuleZone(on: boolean): void;
  onReach(on: boolean): void;
  onShowStaff(on: boolean): void;
  onShowVisitors(on: boolean): void;
  onShowCloset(on: boolean): void;
  onShowSigns(on: boolean): void;
  onReset(): void;
  onResetLayout(): void;
}

export interface Panel {
  setStats(stats: Stats): void;
  setView(mode: ViewMode): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  node.append(...children);
  return node;
}

export function buildPanel(root: HTMLElement, h: PanelHandlers): Panel {
  const panel = el('div', { class: 'panel' });

  const collapse = el(
    'button',
    { type: 'button', 'aria-label': 'Collapse panel' },
    '–',
  );
  collapse.addEventListener('click', () => {
    const c = panel.classList.toggle('collapsed');
    collapse.textContent = c ? '+' : '–';
  });
  panel.append(
    el(
      'div',
      { class: 'head' },
      el(
        'div',
        {},
        el('h1', {}, 'SHOT 2027 ', el('span', {}, 'Booth 44347')),
        el('p', { class: 'sub' }, 'Hall G · 10x20 corner · layout A'),
      ),
      collapse,
    ),
  );

  // View
  const walkBtn = el(
    'button',
    { type: 'button', 'aria-pressed': 'true' },
    'Walk',
  );
  const planBtn = el(
    'button',
    { type: 'button', 'aria-pressed': 'false' },
    'Plan',
  );
  const setView = (mode: ViewMode) => {
    walkBtn.setAttribute('aria-pressed', String(mode === 'walk'));
    planBtn.setAttribute('aria-pressed', String(mode === 'plan'));
  };
  walkBtn.addEventListener('click', () => {
    setView('walk');
    h.onView('walk');
  });
  planBtn.addEventListener('click', () => {
    setView('plan');
    h.onView('plan');
  });
  panel.append(el('div', { class: 'seg' }, walkBtn, planBtn));

  // Crowd
  const crowdOut = el('output', {}, '1.0×');
  const crowd = el('input', {
    type: 'range',
    id: 'crowd',
    min: '0',
    max: '4',
    step: '0.25',
    value: '1',
  });
  crowd.addEventListener('input', () => {
    crowdOut.textContent = `${Number(crowd.value)
      .toFixed(2)
      .replace(/\.?0+$/, '')}×`;
    h.onCrowd(Number(crowd.value));
  });
  panel.append(
    el(
      'div',
      { class: 'row' },
      el('label', { for: 'crowd' }, 'Crowd'),
      crowd,
      crowdOut,
    ),
  );

  // Wall height
  const heightSeg = el('div', { class: 'seg' });
  const heightBtns = WALL_HEIGHTS.map((v) => {
    const b = el(
      'button',
      { type: 'button', 'aria-pressed': String(v === DEFAULT_WALL_HEIGHT) },
      `${v}' walls`,
    );
    b.addEventListener('click', () => {
      for (const o of heightBtns) o.setAttribute('aria-pressed', 'false');
      b.setAttribute('aria-pressed', 'true');
      h.onHeight(v);
    });
    return b;
  });
  heightSeg.append(...heightBtns);
  panel.append(
    el(
      'div',
      { class: 'row' },
      el('label', {}, `Height (Hall G max ${HALL_G_MAX_HEIGHT}')`),
    ),
    heightSeg,
  );

  // Toggles
  const zone = el('input', { type: 'checkbox', id: 'zone' });
  zone.addEventListener('change', () => h.onRuleZone(zone.checked));
  panel.append(
    el(
      'div',
      { class: 'row toggle' },
      el('label', { for: 'zone' }, "Show 10' neighbor rule zone"),
      zone,
    ),
  );
  const toggle = (
    id: string,
    text: string,
    on: boolean,
    fn: (v: boolean) => void,
  ) => {
    const input = el('input', {
      type: 'checkbox',
      id,
      ...(on ? { checked: '' } : {}),
    });
    input.addEventListener('change', () => fn(input.checked));
    panel.append(
      el('div', { class: 'row toggle' }, el('label', { for: id }, text), input),
    );
  };
  toggle('reach', 'Show staff reach', true, h.onReach);
  toggle('show-staff', 'Show staff', true, h.onShowStaff);
  toggle('show-visitors', 'Show visitors', true, h.onShowVisitors);
  toggle('show-closet', 'Show storage closet', true, h.onShowCloset);
  toggle('show-signs', "Show 'Try it live' signs", true, h.onShowSigns);

  // Actions
  const resetSim = el(
    'button',
    { type: 'button', class: 'btn' },
    'Clear crowd',
  );
  resetSim.addEventListener('click', () => h.onReset());
  const resetLayout = el(
    'button',
    { type: 'button', class: 'btn' },
    'Reset layout',
  );
  resetLayout.addEventListener('click', () => h.onResetLayout());
  panel.append(el('div', { class: 'row' }, resetSim, resetLayout));

  // Stats
  const stat = (label: string) => {
    const b = el('b', {}, '0');
    const wrap = el('div', {}, b, el('small', {}, label));
    return { b, wrap };
  };
  const inBooth = stat('in the booth now');
  const greeted = stat('greeted by staff');
  const missed = stat('stopped, never greeted');
  const demos = stat('demos finished');
  const queue = stat('longest kiosk queue');
  const passed = stat('walked past');
  missed.wrap.classList.add('warn');
  panel.append(
    el(
      'div',
      { class: 'stats' },
      inBooth.wrap,
      greeted.wrap,
      missed.wrap,
      demos.wrap,
      queue.wrap,
      passed.wrap,
    ),
  );

  panel.append(
    el(
      'p',
      { class: 'help' },
      'Walk: click the view, then ',
      el('kbd', {}, 'W'),
      el('kbd', {}, 'A'),
      el('kbd', {}, 'S'),
      el('kbd', {}, 'D'),
      ' and the mouse. ',
      el('kbd', {}, 'Shift'),
      ' to hurry. On a phone, drag the left half to move and the right half to look. Plan: drag the counter, kiosks and staff.',
    ),
  );
  panel.append(
    el(
      'p',
      { class: 'facts' },
      `Usable 19'6" × 9'3" per the SHOT 2027 display regulations. Aisles drawn at ${AISLE_WIDTH}', which the rules do not state. Attendees and staff are generic figures.`,
    ),
  );

  root.append(panel);
  return {
    setView,
    setStats(s) {
      inBooth.b.textContent = String(s.inBooth);
      greeted.b.textContent = String(s.greeted);
      missed.b.textContent = String(s.missed);
      demos.b.textContent = String(s.demosDone);
      queue.b.textContent = String(Math.max(0, ...Object.values(s.maxQueue)));
      passed.b.textContent = String(s.passedBy);
    },
  };
}
